import React, { PropsWithChildren, useMemo, useState } from "react";
import {
  usePerformance,
  usePortfolio,
  usePortfolioAllocation,
  useRecommendedBenchmarks,
  useSnapshot,
} from "../api";
import { useGlobalPeriod } from "./GlobalPeriodContext";
import {
  BasePerfSummary,
  PortfolioAndBenchmarkPerfDataDtoV3,
  PortfolioPerformanceApi,
  RecommendedBenchmark,
} from "@iliotech/generated-api-v3/__generated/index";
import { API_BASE } from "../../constants/constants";
import { useQuery } from "react-query";
import {
  ClickableLegendItem,
  IBenchmarkItem,
  mapBenchmarkItems,
  mapPerformanceV3,
} from "@iliotech/component-library";
import { BENCHMARK_COLORS, PORTFOLIO_BENCHMARK_COLOR } from "../../utils";
import { get, uniqBy } from "lodash";
import {
  IGlobalPeriodContext,
  IStructureGrouping,
  useReferenceData,
  useStructureGrouping,
} from "../../main";
import { IChartDataItemV2 } from "@iliotech/types";

const TOTAL_COLOR = "var(--illio-blue)";

export const PerformanceContext =
  React.createContext<ReturnType<typeof usePerformanceHook> | undefined>(
    undefined
  );

interface IOwnProps {
  portfolioId: string;
  isReport?: boolean;
}

export const PerformanceContextProvider = ({
  portfolioId,
  children,
  isReport,
}: PropsWithChildren<IOwnProps>) => {
  const value = usePerformanceHook(portfolioId, isReport);

  return (
    <PerformanceContext.Provider value={value}>
      {children}
    </PerformanceContext.Provider>
  );
};

export const usePerformanceHook = (
  portfolioId: string,
  isReport?: boolean,
  overridePeriod?: IGlobalPeriodContext,
  structureContext?: IStructureGrouping
) => {
  const { periodForRequest } = overridePeriod || useGlobalPeriod();
  const allocations = usePortfolioAllocation(portfolioId!);
  const snapshots = useSnapshot(portfolioId);
  const portfolioRequest = usePortfolio(portfolioId!);
  const { getAllocationLabel } = useReferenceData();
  const portfolioInfo = portfolioRequest?.data?.data;
  const totalColor = isReport ? "#18a0fb" : TOTAL_COLOR;
  const getCustodianId = (label?: string) => {
    if (!label) {
      return undefined;
    }
    const whatFound = portfolioInfo?.custodians?.find(
      (item) => item.code === label
    );

    return whatFound?.id;
  };
  const { selectedGroup, grouping, setSelectedGroup } =
    structureContext || useStructureGrouping();

  const performance = usePerformance(
    portfolioId!,
    periodForRequest,
    grouping.key === "Custodian" || !selectedGroup?.code
      ? undefined
      : grouping.key,
    getCustodianId(selectedGroup?.code)
  );

  const positions = useMemo(() => {
    const grouped = selectedGroup?.code;

    if (!grouped) {
      return snapshots?.data?.data;
    }

    const field = grouping?.field;

    return snapshots?.data?.data?.filter((item) => {
      return get(item, field) === grouped;
    });
  }, [snapshots, grouping, selectedGroup]);

  const performanceByAllocation = usePerformance(
    portfolioId!,
    periodForRequest,
    grouping.key
  );

  const pnlPerformance = performance?.data?.data;
  const portfolioBenchmarkCode = performance?.data?.data?.benchmarkCode;

  const api = new PortfolioPerformanceApi(undefined, `${API_BASE}/api`);

  const from = periodForRequest?.from;
  const to = periodForRequest?.to;

  const [availableBenchmarks, setAvailableBenchmarks] = useState<
    RecommendedBenchmark[]
  >([]);
  const [selectedBenchmarks, setSelectedBenchmarks] = useState<string[]>([]);

  const defaultBenchmarks = useRecommendedBenchmarks(portfolioId!);
  const filteredDefaultBenchmarks = React.useMemo(() => {
    // filter out duplicate benchmarks
    return defaultBenchmarks?.data?.data?.recommendedBenchmarks.filter(
      (item) => item.ticker !== portfolioBenchmarkCode
    );
  }, [defaultBenchmarks, portfolioBenchmarkCode]);

  React.useEffect(() => {
    setAvailableBenchmarks(filteredDefaultBenchmarks || []);
  }, [JSON.stringify(filteredDefaultBenchmarks)]);

  React.useEffect(() => {
    if (portfolioBenchmarkCode && selectedBenchmarks.length === 0) {
      setSelectedBenchmarks([portfolioBenchmarkCode!]);
    }
  }, [selectedBenchmarks.length, portfolioBenchmarkCode]);

  const portfolioBenchmarks: IBenchmarkItem[] = React.useMemo(() => {
    if (!pnlPerformance?.name) return [];
    const injectDefaults = [
      {
        label: pnlPerformance?.name,
        value: pnlPerformance?.name,
        color: PORTFOLIO_BENCHMARK_COLOR,
        disabled: true,
        active: true,
      },
      {
        label: pnlPerformance?.benchmarkName,
        value: pnlPerformance?.benchmarkCode,
        color: BENCHMARK_COLORS[0],
        disabled: true,
        active: true,
      },
    ];
    if (!availableBenchmarks) {
      return injectDefaults;
    }
    const selectableBenchmarks = (availableBenchmarks || []).map(
      (item, index) => ({
        value: item.ticker,
        label: item.name,
        color: BENCHMARK_COLORS[index + 1],
      })
    );
    return uniqBy([...injectDefaults, ...selectableBenchmarks], (b) => b.label);
  }, [
    availableBenchmarks,
    pnlPerformance?.benchmarkName,
    pnlPerformance?.benchmarkCode,
    pnlPerformance?.name,
  ]);

  const updateBenchmarks = (newBenchmarks: RecommendedBenchmark[]) => {
    // we want to add the new optional benchmarks to the list
    // but we want to pre-select only the ones we just added
    // so we filter out the ones that are already in the list
    const actualNewBenchmarks = newBenchmarks.filter(
      (item) => !availableBenchmarks.find((i) => i.ticker === item.ticker)
    );
    setSelectedBenchmarks([
      ...selectedBenchmarks,
      ...actualNewBenchmarks.map((item) => item.ticker),
    ]);
    // here we can set the availableBenchmarks to all the ones that are selected in the modal
    setAvailableBenchmarks(newBenchmarks);
  };

  const query = useQuery(
    [
      `portfolio-performance-benchmarks`,
      { portfolioId, selectedBenchmarks, from, to },
    ],
    () =>
      api.getBenchmarkPerformance(
        portfolioId,
        new Set(selectedBenchmarks),
        from,
        to
      ),
    { enabled: !!selectedBenchmarks?.length && !!portfolioId }
  );

  const onBenchmarkItemDelete = (
    e: React.MouseEvent<SVGElement, MouseEvent>,
    itemId: string
  ) => {
    e.stopPropagation();

    if (selectedBenchmarks.includes(itemId)) {
      setSelectedBenchmarks((selectedItems) =>
        selectedItems.filter((item) => item !== itemId)
      );
    }
    setAvailableBenchmarks((prev) =>
      prev.filter((item) => item.ticker !== itemId)
    );
  };

  const mapAllocationPerformance = (
    data: PortfolioAndBenchmarkPerfDataDtoV3 | undefined,
    portfolioData: PortfolioAndBenchmarkPerfDataDtoV3 | undefined
  ) => {
    const groupCode = selectedGroup?.code;
    if (!data?.allocationLevelData?.allocationData) {
      return {};
    }

    const allocationData = selectedGroup?.code
      ? {
          [selectedGroup?.code]:
            data?.allocationLevelData?.allocationData?.[selectedGroup?.code],
        }
      : data.allocationLevelData.allocationData;
    const result: { [key: string]: IChartDataItemV2[] } = {};

    Object.entries(allocationData).forEach(([allocationName, allocation]) => {
      result[allocationName] = allocation.portfolioData.map((item: any) => ({
        value: item.twr,
        date: new Date(item.date),
        name: allocationName,
        category: allocationName,
        id: allocationName,
        color: grouping.getColor?.(allocationName) || "black", // You'll need to implement this function
      }));
    });

    console.log({
      groupCode,
      shouldBe:
        portfolioData?.allocationLevelData?.allocationData?.[groupCode!]
          ?.portfolioData,
    });
    //@ts-ignore
    result["Total"] = (
      !!groupCode
        ? portfolioData?.allocationLevelData?.allocationData?.[groupCode]
            ?.portfolioData
        : portfolioData?.portfolioData
    )?.map((item: any) => ({
      value: item.twr,
      date: new Date(item.date),
      name: "Total",
      category: "Total",
      id: "Total",
      color: totalColor,
    }));

    return result;
  };

  const getLegendItems = (
    dataPassed: PortfolioAndBenchmarkPerfDataDtoV3 | undefined,
    portfolioData: PortfolioAndBenchmarkPerfDataDtoV3 | undefined
  ) => {
    const legendItems: ClickableLegendItem[] = [];
    const data = dataPassed?.allocationLevelData?.allocationData;
    const grouped = selectedGroup?.code;
    if (!data) {
      return legendItems;
    }

    const getLegendValue = (
      item: BasePerfSummary | PortfolioAndBenchmarkPerfDataDtoV3
    ) => {
      // sort by latest date
      return item?.portfolioData?.sort(
        (a, b) => new Date(b?.date).getTime() - new Date(a?.date).getTime()
      )[0]?.twr;
    };

    const totalLegendValue = getLegendValue?.(
      !!grouped
        ? dataPassed.allocationLevelData?.allocationData?.[grouped]!
        : portfolioData!
    );
    legendItems.push({
      label: "Total",
      color: TOTAL_COLOR,
      value:
        totalLegendValue?.toLocaleString(undefined, {
          maximumFractionDigits: 2,
          minimumFractionDigits: 2,
        }) + "%",
      rawValue: totalLegendValue,
      onClick: () => {
        setSelectedGroup?.(undefined);
      },
    });

    Object.keys(data).forEach((key) => {
      legendItems.push({
        label: getAllocationLabel(key),
        color: grouping.getColor?.(key) || "black",
        value:
          getLegendValue(data[key])?.toLocaleString(undefined, {
            maximumFractionDigits: 2,
            minimumFractionDigits: 2,
          }) + "%",
        rawValue: getLegendValue(data[key]),
        onClick: () => {
          setSelectedGroup?.({ code: key, label: key });
        },
      });
    });

    return legendItems;
  };

  const { allocationPerformance, legendItems } = React.useMemo(() => {
    return {
      allocationPerformance: mapAllocationPerformance(
        performanceByAllocation?.data?.data,
        performance?.data?.data
      ),
      legendItems: getLegendItems(
        performanceByAllocation?.data?.data,
        performance?.data?.data
      ),
    };
  }, [
    JSON.stringify(performanceByAllocation?.data?.data),
    JSON.stringify(performance?.data?.data),
    selectedGroup,
    grouping,
  ]);

  const toggleBenchmarkLineVisibility = (itemId: string) => {
    if (selectedBenchmarks.includes(itemId)) {
      setSelectedBenchmarks((prev) => prev.filter((item) => item !== itemId));
    } else {
      setSelectedBenchmarks((selectedItems) => [...selectedItems, itemId]);
    }
  };

  const riskAssetView = portfolioInfo?.riskAssetView;

  const isLoading = [snapshots, performance, portfolioRequest].some(
    (item) => item.isLoading
  );

  const benchmarkData = query.data?.data;

  const performanceLineChartData = React.useMemo(() => {
    const group =
      grouping.key === "Custodian" ? undefined : selectedGroup?.code;
    const initialPnlPerformance = mapPerformanceV3(pnlPerformance, group);
    if (benchmarkData?.performanceBenchmarks && pnlPerformance) {
      const benchmarksToAdd = mapBenchmarkItems(
        benchmarkData?.performanceBenchmarks,
        portfolioBenchmarks
      );
      return [...initialPnlPerformance, ...benchmarksToAdd];
    }
    return [];
  }, [benchmarkData, portfolioBenchmarks, pnlPerformance, selectedGroup]);

  return {
    performanceByAllocation: allocationPerformance,
    legendItems,
    positions,
    portfolioController: {
      snapshots,
      portfolioInfo,
      riskAssetView,
      performanceLoading: isLoading,
      pnlPerformance,
      performanceLineChartData,
      allocations: allocations?.data?.data,
    },
    benchmarkController: {
      selectedBenchmarks,
      filteredDefaultBenchmarks,
      onBenchmarkItemDelete,
      toggleBenchmarkLineVisibility,
      updateBenchmarks,
      benchmarksLoading: query.isLoading,
      portfolioBenchmarks,
    },
  };
};

export const usePortfolioBenchmarkPerformance = () => {
  const context = React.useContext(PerformanceContext);
  if (typeof context === "undefined") {
    throw new Error(
      "PerformanceContext can only be accessed from within it's provider"
    );
  }
  return context;
};
